/**
 * Inventory Batch Validation Schemas
 * Zod schemas for form validation
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { z } from 'zod';
import {
  ProductType,
  QuantityUnit,
  QualityGrade,
  isPurityValidForGrade
} from '@/types/inventory';

// ============================================
// CREATE BATCH SCHEMA
// ============================================

/**
 * Zod schema for batch creation form
 * Validates all fields with comprehensive error messages
 */
export const createBatchSchema = z
  .object({
    // Product Type - Required enum
    productType: z.nativeEnum(ProductType, {
      errorMap: () => ({ message: 'Please select a product type' })
    }),

    // Production Date - Required, cannot be in future
    productionDate: z.date({
      required_error: 'Production date is required',
      invalid_type_error: 'Please select a valid production date'
    }).refine(
      (date) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today
        return date <= today;
      },
      {
        message: 'Production date cannot be in the future'
      }
    ),

    // Expiry Date - Required, must be in future
    expiryDate: z.date({
      required_error: 'Expiry date is required',
      invalid_type_error: 'Please select a valid expiry date'
    }).refine(
      (date) => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0); // Start of tomorrow
        return date >= tomorrow;
      },
      {
        message: 'Expiry date must be at least tomorrow'
      }
    ),

    // Initial Quantity - Required, must be greater than 0
    initialQuantity: z
      .number({
        required_error: 'Initial quantity is required',
        invalid_type_error: 'Please enter a valid number'
      })
      .positive('Initial quantity must be greater than 0')
      .max(999999999.999, 'Initial quantity cannot exceed 999,999,999.999')
      .refine(
        (val) => {
          // Check decimal places (max 3)
          const decimalPlaces = (val.toString().split('.')[1] || '').length;
          return decimalPlaces <= 3;
        },
        {
          message: 'Initial quantity can have maximum 3 decimal places'
        }
      ),

    // Quantity Unit - Required enum
    quantityUnit: z.nativeEnum(QuantityUnit, {
      errorMap: () => ({ message: 'Please select a quantity unit' })
    }),

    // Quality Grade - Required enum (A, B, or C)
    qualityGrade: z.nativeEnum(QualityGrade, {
      errorMap: () => ({ message: 'Please select a quality grade' })
    }),

    // Purity Percentage - Required, 0-100 with 2 decimal places
    purityPercentage: z
      .number({
        required_error: 'Purity percentage is required',
        invalid_type_error: 'Please enter a valid percentage'
      })
      .min(0, 'Purity percentage must be at least 0%')
      .max(100, 'Purity percentage cannot exceed 100%')
      .refine(
        (val) => {
          // Check decimal places (max 2)
          const decimalPlaces = (val.toString().split('.')[1] || '').length;
          return decimalPlaces <= 2;
        },
        {
          message: 'Purity percentage can have maximum 2 decimal places'
        }
      ),

    // Pressure - Optional, but must be non-negative if provided
    pressure: z
      .number({
        invalid_type_error: 'Please enter a valid pressure value'
      })
      .nonnegative('Pressure must be non-negative')
      .max(99999999.99, 'Pressure cannot exceed 99,999,999.99')
      .refine(
        (val) => {
          // Check decimal places (max 2)
          const decimalPlaces = (val.toString().split('.')[1] || '').length;
          return decimalPlaces <= 2;
        },
        {
          message: 'Pressure can have maximum 2 decimal places'
        }
      )
      .optional()
      .or(z.literal('').transform(() => undefined)) // Allow empty string
      .or(z.nan().transform(() => undefined)), // Handle NaN

    // Storage Location - Required, max 255 characters
    storageLocation: z
      .string({
        required_error: 'Storage location is required'
      })
      .min(1, 'Storage location is required')
      .max(255, 'Storage location must not exceed 255 characters')
      .trim(),

    // PESO Compliant - Optional boolean
    pesoCompliant: z.boolean().optional().default(false)
  })
  .refine(
    (data) => {
      // Cross-field validation: Expiry date must be after production date
      if (data.productionDate && data.expiryDate) {
        return data.expiryDate > data.productionDate;
      }
      return true;
    },
    {
      message: 'Expiry date must be after production date',
      path: ['expiryDate']
    }
  )
  .refine(
    (data) => {
      // Validate purity percentage matches selected quality grade
      if (data.qualityGrade && data.purityPercentage !== undefined) {
        return isPurityValidForGrade(data.purityPercentage, data.qualityGrade);
      }
      return true;
    },
    {
      message: 'Purity percentage does not match the selected quality grade',
      path: ['purityPercentage']
    }
  )
  .refine(
    (data) => {
      // Pressure is required for COMPRESSED and BIO_CNG products
      if (
        data.productType === ProductType.COMPRESSED ||
        data.productType === ProductType.BIO_CNG
      ) {
        return data.pressure !== undefined && data.pressure > 0;
      }
      return true;
    },
    {
      message: 'Pressure is required for COMPRESSED and BIO_CNG products',
      path: ['pressure']
    }
  );

/**
 * Infer TypeScript type from schema
 */
export type CreateBatchFormData = z.infer<typeof createBatchSchema>;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert form data to API request format
 * Transforms Date objects to ISO strings
 */
export function formDataToApiRequest(
  formData: CreateBatchFormData
): import('@/types/inventory').CreateBatchRequest {
  return {
    productType: formData.productType,
    productionDate: formData.productionDate.toISOString().split('T')[0], // yyyy-MM-dd
    expiryDate: formData.expiryDate.toISOString().split('T')[0], // yyyy-MM-dd
    initialQuantity: formData.initialQuantity,
    quantityUnit: formData.quantityUnit,
    qualityGrade: formData.qualityGrade,
    purityPercentage: formData.purityPercentage,
    pressure: formData.pressure,
    storageLocation: formData.storageLocation,
    pesoCompliant: formData.pesoCompliant || false
  };
}

/**
 * Get minimum expiry date based on production date
 * Expiry must be at least 1 day after production
 */
export function getMinExpiryDate(productionDate: Date | undefined): Date {
  if (!productionDate) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  }

  const minExpiry = new Date(productionDate);
  minExpiry.setDate(minExpiry.getDate() + 1);
  minExpiry.setHours(0, 0, 0, 0);
  return minExpiry;
}

/**
 * Get maximum production date (today)
 */
export function getMaxProductionDate(): Date {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return today;
}
